export default {
  app: {
    title: 'Home',
    description: 'Dashboard and overview',
  },
  common: {
    title: 'Home',
    description: 'Dashboard and overview',
  },
  filled: {
    greeting: 'Hello {name},',
    greetingNoName: 'Hello,',
    invitations: [
      'good to see you again.',
      'glad to have you back.',
      'happy to see you here.',
      'content first.',
      'your audience is waiting.',
    ],
    lastUpload: 'Your last media was added {relative}, want to keep going?',
  },
  inviteBanner: {
    title: 'Invite your team',
    subtitle: 'Collaborate on your projects and share your content in a few clicks.',
    cta: 'Invite',
    later: 'Later',
  },
  welcomeVideo: {
    title: 'Discover Videas in 2 minutes',
    subtitle: 'Upload, caption, publish and monetize your content — a full tour of the platform.',
    watch: 'Watch',
    dismiss: 'Hide',
  },
  empty: {
    badge: 'Welcome to Videas',
    greeting: 'Hi {name},',
    greetingNoName: 'Hi,',
    invitation: 'shall we begin?',
    subtitle: 'Your space is empty for now. Follow the steps below to fill it and discover everything Videas can do for you.',
    dropzone: {
      title: 'Drop your first files here',
      subtitle: 'Drag and drop from your desktop, or browse your folders. Videos, images, audio, PDF — up to 2 GB per file.',
      browse: 'Browse',
    },
    firstSteps: {
      lead: 'A few ways in to get started, in whatever order suits you.',
      afterImport: 'After your first upload',
      guide: 'The getting started guide',
    },
  },
  onboarding: {
    // The way back. Only there while no speech media has been imported: after
    // Audio overlay: only the sentences that name the medium.
    // `useOnboardingCopy` tries `home.onboarding.audio.<key>` before the
    // original, so anything absent here is written once and once only.
    audio: {
      tracks: {
        sharing: {
          label: 'Send a recording to someone',
          goal: 'your first listen',
        },
        subtitles: {
          label: 'Transcribe a recording',
          goal: 'your transcript',
          deliverable: 'your transcript',
        },
        websites: {
          // "Podcast" is their word for it; the deliverable does not promise
          // distribution, since there is no RSS feed anywhere.
          label: 'Put your podcast online',
          goal: 'your podcast online',
          deliverable: 'the public address of your listening page',
        },
        embeds: {
          label: 'Embed an episode on your site',
        },
      },
      actions: {
        subtitle_generate: {
          title: 'Generate the transcript',
          cta: 'Generate the transcript',
          suggestion: 'Transcribe your episode',
        },
        share_statistics_read: {
          title: 'See your first listen',
        },
      },
      steps: {
        hints: {
          share_create: 'Private, dated — and you see who listens',
        },
      },
      scenes: {
        subtitle_generate: {
          title: 'Transcribe your recording',
          note: 'About 30 seconds for 5 minutes of audio.',
          busyTitle: 'We are listening to your recording',
          busyNote: 'Much faster than playing the whole thing again.',
          doneNote: 'Your transcript exists — all that is left is reviewing it.',
          error: {
            line: 'We could not generate a transcript for this recording. Nothing was taken from your balance.',
          },
        },
        subtitle_edit: {
          title: 'Review your transcript',
          line: 'The editor plays the sound while you read. Doubtful passages are flagged, the rest is already fine.',
          error: {
            title: 'Scribe could not open your recording',
          },
        },
        share_create: {
          title: 'Send your recording',
          error: {
            line: 'The request did not make it through. Your file has not moved.',
          },
        },
        embed_code_copy: {
          line: 'One line to paste into your page. The player fits the available width, and listens from your site show up here.',
          error: {
            line: 'Your file has to be encoded before it can be embedded anywhere.',
          },
        },
        website_create: {
          // Sound has no thumbnail: without a cover the published channel is a
          // grey grid, and that grid is the deliverable shown at the end.
          note: 'Your address will end in .websites.videas.com. Plan for a cover: sound has no image.',
        },
        errors: {
          encodingFailed: {
            title: 'We could not encode your file',
            line: 'The file may be damaged, or in a format we cannot read. An MP3 always works.',
          },
        },
      },
      finish: {
        tracks: {
          subtitles: {
            title: 'Your transcript is',
            line: 'Transcribed in seconds, reviewed by you. Export it, or keep it attached to the episode.',
          },
          embeds: {
            line: 'The code fits the width of your page, and listens from your site show up in the app.',
          },
        },
        suggestions: {
          subtitle_generate: 'Thirty seconds, and your link reads as well as it plays.',
          share_create: 'A private link, and you get to see who listened.',
          website_host_connect: 'mypodcast.com, rather than mypodcast.websites.videas.com.',
        },
      },
    },
    locked: 'After "{step}"',
    skipped: 'Skipped — do it whenever you want',
    host: {
      resume: 'shall we carry on?',
      done: 'nicely done.',
      goal: 'Your goal: {goal} — about {minutes} min.',
    },
    router: {
      needsAsset: 'Import a media first — we pick up right after.',
      // The import ticks as soon as the bytes land, encoding carries on behind.
      // Answering "import a media first" then says the opposite of what the
      // column has just announced.
      assetEncoding: 'Your media is still encoding — we pick up as soon as it is ready.',
      needsShare: 'Create your share link first.',
      needsChannel: 'Create your channel first.',
      needsWorkspace: 'No workspace to open just yet.',
      notYetAvailable: 'This step is not open yet. We will come and get you as soon as it is.',
      failed: 'That would not open. Try again in a moment, your progress is kept.',
    },
    steps: {
      title: 'Getting started',
      progressLabel: 'Progress: {done} of {total}',
      dismiss: 'Hide getting started for good',
      // A step shut while the media finishes: saying "After « Import your first
      // media »" would be wrong, that one is already ticked.
      preparing: 'Getting your media ready',
      remainingOne: 'One step to go before {goal}. About {minutes} min.',
      remaining: '{count} steps to go before {goal}. About {minutes} min.',
      finished: 'All done.',
      account: {
        title: 'Your account is ready',
        hint: 'Welcome to Videas',
      },
      state: {
        done: 'Done',
      },
      // Ticked steps whose work is still running in the background.
      working: {
        asset_upload: 'Encoding — you can carry on',
      },
      hints: {
        asset_upload: 'Drag it in, or browse your folders',
        subtitle_generate: 'Over 100 languages, automatic detection',
        subtitle_edit: 'The editor lines the text up with the audio',
        share_create: 'Private, time-limited — and you see who watches',
        share_statistics_read: 'Who opened it, for how long, on what device',
        website_create: 'A template, a name, a color',
        website_publish: 'It starts as a draft — you decide when to open it',
        embed_code_copy: 'One line to paste into your page',
        member_invite: 'Everyone finds the same media',
        embed_preset_save: 'Color, logo, behavior — for all your players',
        website_host_connect: 'Your own address, instead of the Videas one',
      },
    },
    tracks: {
      sharing: {
        label: 'Send a video to someone',
        goal: 'your first view',
        deliverable: 'the share link',
      },
      subtitles: {
        label: 'Subtitle a video',
        goal: 'your subtitles',
        deliverable: 'the .srt file',
      },
      websites: {
        label: 'Put a channel online',
        goal: 'your channel online',
        deliverable: 'the public address',
      },
      embeds: {
        label: 'Embed a media file on your site',
        goal: 'your embed code',
        deliverable: 'the code to paste',
      },
    },
    actions: {
      // "File" and not "video": the dropzone accepts audio, images and PDF,
      // and the journey never asks for the type — it takes the last imported
      // file. Promising "video" here contradicted the scene's own note, which
      // lists all four types on the same screen.
      asset_upload: {
        title: 'Import your first file',
        cta: 'Import a file',
      },
      subtitle_generate: {
        title: 'Generate the subtitles',
        cta: 'Generate the subtitles',
        suggestion: 'Subtitle your video',
      },
      subtitle_edit: {
        title: 'Review in Scribe',
        cta: 'Open Scribe',
      },
      share_create: {
        title: 'Create a share link',
        cta: 'Create the link',
        suggestion: 'Send the link',
      },
      share_statistics_read: {
        title: 'See your first view',
        cta: 'View statistics',
      },
      website_create: {
        title: 'Create your channel',
        cta: 'Create your channel',
      },
      website_publish: {
        title: 'Publish your channel',
        cta: 'Publish your channel',
      },
      embed_code_copy: {
        title: 'Copy your embed code',
        cta: 'Get the code',
      },
      member_invite: {
        title: 'Invite your team',
        cta: 'Invite teammates',
      },
      embed_preset_save: {
        title: 'Tune the player',
        cta: 'Open player settings',
        suggestion: 'Tune the player',
      },
      website_host_connect: {
        title: 'Connect your domain',
        cta: 'Connect your domain',
        suggestion: 'Connect your domain',
      },
    },
    scenes: {
      eyebrow: 'Step {current} of {total}',
      bonusEyebrow: 'One more thing',
      doneEyebrow: 'Done',
      errorEyebrow: 'This one stalled',
      busyNote: 'We are on it — stay around.',
      errorNote: 'If it happens again, write to us from the help centre and we will look into it with you.',
      asset_upload: {
        title: 'Your first file',
        line: 'Drag a file from your desktop, or browse your folders. We take care of the encoding.',
        cta: 'Choose a file',
        note: 'Videos, images, audio, PDF — up to 2 GB per file.',
        busyTitle: 'We are fetching your file',
        busyLine: 'Your file is on its way. Stay on this page: closing the tab would interrupt the upload.',
        busyNote: 'As soon as it lands we move on — encoding finishes while you carry on.',
        doneNote: 'It is encoded and ready to use.',
        error: {
          title: 'Your import did not make it',
          line: 'The transfer stopped along the way. Your file is untouched on your computer, nothing is lost.',
          cta: 'Resume the import',
        },
      },
      subtitle_generate: {
        title: 'Subtitle your video',
        line: 'Videas detects the language, splits the sentences and lines them up with the audio. You come after to fix whatever you want.',
        cta: 'Pick a language and generate',
        note: 'About 30 seconds for 5 minutes of video.',
        busyTitle: 'We are listening to your video',
        busyLine: 'The transcription is running. Go and do something else, we will be here when you come back.',
        busyNote: 'Much faster than watching the whole thing.',
        doneNote: 'Your subtitles exist — all that is left is reviewing them.',
        error: {
          title: 'The transcription produced nothing',
          line: 'We could not generate subtitles for this video. Nothing was taken from your balance.',
          cta: 'Run the transcription again',
        },
      },
      subtitle_edit: {
        title: 'Review your subtitles',
        line: 'The editor plays the video while you read. Doubtful passages are flagged, the rest is already fine.',
        cta: 'Open Scribe',
        note: 'Your corrections are saved as you type.',
        doneNote: 'Reviewed, corrected, saved.',
        error: {
          title: 'Scribe could not open your video',
          line: 'The editor failed to load the audio track. This is usually temporary.',
          cta: 'Open Scribe again',
        },
      },
      share_create: {
        title: 'Send your video',
        line: 'A private link, with an expiry date, a password and an opening limit if you want. Every opening shows up in your statistics.',
        cta: 'Create the share link',
        note: 'The link expires after 7 days by default.',
        doneNote: 'Your link is ready — all that is left is sending it.',
        error: {
          title: 'The link was not created',
          line: 'The request did not make it through. Your video has not moved.',
          cta: 'Create the link again',
        },
      },
      share_statistics_read: {
        // "opened", not "watched": the counter goes up when the share page
        // loads (plan § 8). The other three lines of this block already said
        // "opening"; only the title promised playback.
        title: 'See who opened your link',
        line: 'Every opening of your link is counted: when, for how long, on which device. All your links live in the same place.',
        cta: 'Open my shares',
        note: 'Waiting for the first opening.',
        doneNote: 'Your link has been opened: the loop is complete.',
        error: {
          title: 'Your statistics are not loading',
          line: 'The measurement service did not answer. Your openings are still being counted, nothing is lost.',
          cta: 'Try again',
        },
      },
      embed_code_copy: {
        title: 'Get your embed code',
        line: 'One line to paste into your page. The player fits the available width, and plays from your site show up here.',
        cta: 'Get the code',
        note: 'Script or iframe, your call.',
        doneNote: 'The code is copied — it is only waiting for your page.',
        error: {
          title: 'The code could not be generated',
          line: 'Your video has to be encoded before it can be embedded anywhere.',
          cta: 'Try again',
        },
      },
      website_create: {
        title: 'Create your channel',
        line: 'You pick a template — it decides the structure of the site, not just the colours — then a name and a colour. Your media fill in the rest.',
        cta: 'Create my channel',
        note: 'Your address will end in .websites.videas.com.',
        busyTitle: 'We are building your channel',
        busyLine: 'We are assembling the pages, the menu and your catalogue.',
        busyNote: 'A few seconds, no more.',
        doneNote: 'Your channel exists. It is still a draft.',
        error: {
          title: 'Your channel was not created',
          line: 'The creation stopped along the way. Nothing was published, your media are untouched.',
          cta: 'Start over',
        },
      },
      website_publish: {
        title: 'Publish your channel',
        line: 'Your channel exists but stays a draft: nobody can see it yet. You will be able to put it back into draft whenever you want.',
        cta: 'Publish my channel',
        note: 'Publishing a channel is a {price} per month subscription.',
        noteContract: 'Publishing a channel is part of your contract: nothing is charged here.',
        noteOffered: 'Your channel is free of charge: nothing is taken for it.',
        busyTitle: 'We are putting your channel online',
        busyLine: 'We are publishing the pages and opening the address to the public.',
        busyNote: 'A few seconds, no more.',
        doneNote: 'It is online, at its public address.',
        error: {
          title: 'The publication did not go through',
          line: 'Your channel stayed a draft: nothing was shown to the public.',
          cta: 'Publish again',
        },
      },
      member_invite: {
        title: 'Invite your team',
        line: 'Everyone you invite finds your space, its folders and its shares. You choose their role: viewer, editor or administrator.',
        cta: 'Invite my team',
        note: 'If this is not the moment, we will keep it for later.',
        doneNote: 'Your invitation is on its way.',
        error: {
          title: 'The invitation did not go out',
          line: 'The address may be misspelled, or the person may already belong to your organisation.',
          cta: 'Try the invitation again',
        },
      },
      embed_preset_save: {
        title: 'Tune the look of the player',
        line: 'Colour, logo, playback behavior. It becomes your default player: all your embeds will follow.',
        cta: 'Customise the player',
        note: 'Changeable at any time, without touching your code again.',
        doneNote: 'Your preset is saved.',
        error: {
          title: 'The player settings will not open',
          line: 'The preset editor did not answer. Your embeds keep their current look.',
          cta: 'Try again',
        },
      },
      website_host_connect: {
        title: 'Connect your domain',
        line: 'Your own address instead of the one we gave you. You add two records at your host, we check the rest.',
        cta: 'Connect my domain',
        note: 'A connected domain is {domainPrice} per month on top.',
        noteContract: 'A connected domain is part of your contract: nothing is charged here.',
        noteOffered: 'A connected domain is free of charge for you: nothing is taken for it.',
        doneNote: 'Your domain answers.',
        error: {
          title: 'Your domain could not be connected',
          line: 'The check failed. Your channel is still reachable at its current address.',
          cta: 'Try again',
        },
      },
      errors: {
        uploadFailed: {
          title: 'Your import stopped along the way',
          line: 'The connection dropped before the transfer finished. Your file is untouched on your computer.',
          cta: 'Resume the import',
          ctaNewFile: 'Import another file',
          ctaNewImport: 'Import it again',
          ctaTopUp: 'Top up the balance',
          lines: {
            network: 'The connection dropped before the transfer finished. Check your network: the transfer resumes where it stopped, and your file is safe on your computer.',
            interrupted: 'The transfer was interrupted along the way. Resume it: it picks up from what the server already received.',
            tooLarge: 'This file is larger than we accept. Try a lighter version.',
            refused: 'The server refused this file. Check that it is a readable media file: an MP4 or an MP3 always works.',
            forbidden: 'You may no longer add files here. Your session may have expired: sign in again.',
            destinationGone: 'The workspace this file was going into no longer exists. Import it again: it will go into one that does.',
            rateLimited: 'Too many requests at once. Wait a moment, then resume the import.',
            creditsExhausted: 'Your organization\'s credit balance does not cover this import. Top it up: the import starts again after.',
            budgetExceeded: 'A spending limit set on your organization stops this import. Whoever manages billing can raise it.',
            session: 'The upload expired on the server. Start it again: it will restart from the beginning.',
            timedOut: 'The server stopped waiting before the transfer finished. Resume the import in a moment; a steadier connection helps.',
            server: 'Our server could not receive your file. Retry in a moment; if it keeps failing, contact support.',
          },
        },
        // Known at encode time: the probe found no audio stream. The step
        // closes before the dialog, rather than letting a generation run only
        // to come back empty-handed.
        noSpeech: {
          title: 'This media has no sound',
          line: 'There is no audio track to listen to, so nothing to transcribe. Send something with someone speaking and we will take care of it.',
          cta: 'Import another media',
        },
        encodingFailed: {
          title: 'We could not encode your video',
          line: 'The file may be damaged, or in a format we cannot read. An MP4 always works.',
          cta: 'Import another file',
        },
        transcriptionEmpty: {
          title: 'We did not hear anyone speak',
          line: 'The transcription came back empty: the audio track may be silent, or the language may have been misread.',
          cta: 'Pick the language yourself',
        },
        dnsUnresolved: {
          title: 'Your domain does not point to us yet',
          line: 'DNS records sometimes take a few hours to propagate. Your channel is still reachable at its current address.',
          cta: 'Check again',
        },
      },
    },
    finish: {
      eyebrow: 'That is it, {name}. You know how now.',
      eyebrowNoName: 'That is it. You know how now.',
      copy: 'Copy',
      copied: 'Copied',
      open: 'Open',
      acknowledge: 'Go to my home',
      acknowledgeChannel: 'Go to my channel',
      note: 'These first steps are leaving your home page. You can bring them back from {help}.',
      noteHelpLink: 'the help centre',
      cost: 'Your channel: {price} per month, no commitment.',
      costOffered: 'Your channel is free of charge.',
      costDetail: 'See the details',
      suggestionCta: 'Do it',
      suggestionDone: 'Done.',
      tracks: {
        sharing: {
          // The counter ticks when the link is opened, not when the video is
          // played (plan § 8): "watched" would promise a figure we do not have.
          title: 'Someone',
          highlight: 'opened your link.',
          line: 'Import, send, measure: the loop is complete. Everything you send from now on will show up in the same place.',
        },
        subtitles: {
          title: 'Your subtitles are',
          highlight: 'ready.',
          line: 'Transcribed in seconds, reviewed by you. Export them, or keep them attached to the video.',
        },
        websites: {
          title: 'Your channel is',
          highlight: 'online.',
          line: 'Your catalogue is published at this address. You can connect your own domain to it whenever you want.',
        },
        embeds: {
          title: 'Your player is',
          highlight: 'ready to paste.',
          line: 'The code fits the width of your page, and plays from your site show up in the app.',
        },
      },
      suggestions: {
        subtitle_generate: 'Thirty seconds, and your link is much easier to watch.',
        share_create: 'A private link, and you get to see who watched.',
        website_host_connect: 'mycourse.com, rather than mycourse.websites.videas.com.',
        embed_preset_save: 'Colour, logo, behavior — it becomes your default player.',
      },
    },
    skip: 'Skip',
    // "opened your link", not "watched your video": the counter goes up when the
    // share page loads, before anyone presses play (plan § 8).
    toasts: {
      firstView: 'Someone just opened your link.',
      transcribed: '{media} transcribed in {seconds}s.',
      seconds: '{n}s',
      minutes: '{n} min',
      minutesSeconds: '{m} min {s}',
    },
    questions: {
      counter: 'Question {current} of {total}',
      done: 'Done',
      track: {
        question: 'What do you want to do first?',
        hint: 'We walk you through it.',
        note: '≈ {minutes} min · you walk away with {deliverable}',
        // No goal offered: nobody can put anything in here, so no track can be
        // played. Say so, rather than showing an empty list.
        none: 'No journey is open in this space yet. Ask your administrator for upload rights, then come back.',
        // The two chips above the goals. They create no track: they pick the
        // wording of the very same four goals.
        mediaLabel: 'Media kind',
        media: {
          video: 'Video',
          audio: 'Audio',
        },
      },
      team: {
        question: 'Are you on your own on Videas?',
        hint: 'With a team, we add a step to invite your teammates.',
        options: {
          solo: 'On my own, for now',
          team: 'With a team',
        },
      },
      source: {
        question: 'How did you hear about us?',
        hint: 'Last question, and it changes nothing about your journey.',
        specify: 'Tell us in a few words',
        options: {
          search: 'Online search',
          recommendation: 'Word of mouth / recommendation',
          social: 'Social media',
          sales: 'Sales contact',
          article: 'Article (blog, press...)',
          tender: 'Tender / RFP',
          other: 'Other',
        },
      },
    },
  },
  statistics: {
    title: 'Statistics',
    subtitle: 'Manage your quotas and understand your audience',
    consumption: 'Consumption',
    lastMonth: 'Last month',
    thisMonth: 'this month',
    vsPreviousPeriod: 'vs previous period',
    vsLastMonth: 'vs last month',
    videoStorage: {
      title: 'Video storage',
    },
    otherFiles: {
      title: 'Other files',
    },
    views: {
      title: 'Views (30 days)',
    },
    bandwidth: {
      title: 'Bandwidth',
    },
    storageUsed: {
      title: 'Storage used',
      detail: '{videos} of video · {audios} of audio',
    },
    distribution: {
      title: 'Asset distribution',
      unit: 'files',
    },
    totalAssets: '{count} files',
    fileCount: '{count} files',
    segments: {
      images: 'Images',
      audios: 'Audios',
      docs: 'Docs',
    },
  },
  apps: {
    title: 'Our tools',
    description: 'Explore our tools to create, manage and share your content.',
    comingSoon: 'Coming soon',
    open: 'Open',
    items: {
      videoGenerator: {
        title: 'Video generator',
        description: 'Create videos from templates',
      },
    },
  },
  blog: {
    title: 'From the blog',
    description: 'Follow our news, updates and video tips.',
    viewAll: 'Read the blog',
  },
  tutorials: {
    title: 'Learn in 5 minutes',
    viewAll: 'View all tutorials',
    items: {
      shareLink: {
        title: 'Share a video with a link',
        excerpt: 'Create a link in three clicks and choose who can watch: expiry, password, downloads.',
      },
      automaticSubtitles: {
        title: 'Generate subtitles automatically',
        excerpt: 'Scribe transcribes your videos in minutes, in more than 100 languages.',
      },
      createChannel: {
        title: 'Create a video channel',
        excerpt: 'Six questions in two minutes, and your channel is live on its own subdomain.',
      },
    },
  },
  faq: {
    items: {
      monetization: {
        question: 'How do you monetize your content?',
        answer: 'You can monetize your videos through direct sales, subscriptions, or integration on a paid channel.',
      },
      addContent: {
        question: 'How to add content to my channel?',
        answer: 'Click "Add video" from your dashboard, or drag and drop your files directly.',
      },
      secureSharing: {
        question: 'Can I share a video securely with someone?',
        answer: 'Yes, you can generate a private link with a password or an expiry date.',
      },
      embedVideos: {
        question: 'Can I embed Videas videos on my website?',
        answer: 'Of course. Each video can be embedded via responsive embed code.',
      },
      addSubtitles: {
        question: 'How to add subtitles to my video?',
        answer: 'Go to the "Subtitles" tab of a video to import or automatically generate files.',
      },
    },
  },
  assets: {
    title: 'Recent media',
    description: 'Quickly access your latest files to organize, share or analyze them.',
    emptyState: {
      title: 'No recent media',
      description: 'Upload your first file to see it appear here.',
      descriptionNoUpload: 'Media from the workspaces you can access will show up here.',
      uploadButton: 'Upload media',
    },
    status: {
      uploading: 'Uploading…',
      uploadingPercent: 'Uploading… {percent}%',
      processing: 'Preparing…',
      stepPercent: '{step} · {percent}%',
      failed: 'Processing failed',
      interrupted: 'Upload interrupted',
      interruptedReason: 'The file never arrived: its upload stopped before the end. Open the media to send it again.',
      awaitingCredit: 'Awaiting credit',
      awaitingCreditReason: 'The credit does not cover this video\'s first month of storage. Top it up: the encoding resumes by itself. Without a top-up, it goes to the trash after seven days.',
    },
  },
}
